import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Checkbox,
  Col,
  DatePicker,
  List,
  Row,
  Select,
  Space,
  Switch,
  Typography
} from 'antd';
import { CrownOutlined } from '@ant-design/icons';
import { NumericInput } from '../common';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ja';
import type { RecurrenceRule } from '../../../../shared/types/work-schedule.types';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
import { trpc } from '../../lib/api';

const { Text } = Typography;

export type RecurrencePattern = RecurrenceRule['pattern'];

const PATTERN_OPTIONS: { value: RecurrencePattern; label: string }[] = [
  { value: 'daily', label: '日次' },
  { value: 'weekly', label: '週次' },
  { value: 'monthly', label: '月次' },
  { value: 'yearly', label: '年次' },
  { value: 'custom', label: 'カスタム' }
];

const WEEKDAY_OPTIONS = [
  { label: '日', value: 0 },
  { label: '月', value: 1 },
  { label: '火', value: 2 },
  { label: '水', value: 3 },
  { label: '木', value: 4 },
  { label: '金', value: 5 },
  { label: '土', value: 6 }
];

const DEFAULT_RULE: RecurrenceRule = {
  enabled: true,
  pattern: 'weekly',
  interval: 1,
  includeChildren: false,
  advanceDays: 90,
  daysOfWeek: [dayjs().day()]
};

function normaliseRule(rule?: RecurrenceRule | null): RecurrenceRule {
  if (!rule) {
    return DEFAULT_RULE;
  }
  return {
    ...DEFAULT_RULE,
    ...rule,
    enabled: true,
    interval: rule.interval || 1,
    advanceDays: rule.advanceDays ?? DEFAULT_RULE.advanceDays,
    daysOfWeek: rule.daysOfWeek ?? DEFAULT_RULE.daysOfWeek,
    dayOfMonth: rule.dayOfMonth,
    monthsOfYear: rule.monthsOfYear
  };
}

function areNumberArraysEqual(a?: number[], b?: number[]): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
}

function areRulesEqual(a: RecurrenceRule, b: RecurrenceRule): boolean {
  return (
    a.pattern === b.pattern &&
    a.interval === b.interval &&
    a.includeChildren === b.includeChildren &&
    a.advanceDays === b.advanceDays &&
    a.customRule === b.customRule &&
    a.dayOfMonth === b.dayOfMonth &&
    areNumberArraysEqual(a.daysOfWeek, b.daysOfWeek) &&
    areNumberArraysEqual(a.monthsOfYear, b.monthsOfYear) &&
    (a.endDate?.getTime() ?? null) === (b.endDate?.getTime() ?? null)
  );
}

function calculateOccurrences(rule: RecurrenceRule, startDate: Dayjs, limit = 5): Dayjs[] {
  const results: Dayjs[] = [];
  if (!rule.enabled) {
    return results;
  }

  const endBoundary = rule.endDate ? dayjs(rule.endDate) : startDate.add(rule.advanceDays ?? 90, 'days');
  let cursor = startDate;

  const isPastBoundary = (current: Dayjs) => current.isAfter(endBoundary);

  while (results.length < limit) {
    switch (rule.pattern) {
      case 'daily': {
        cursor = cursor.add(rule.interval, 'days');
        break;
      }
      case 'weekly': {
        const days = (rule.daysOfWeek && rule.daysOfWeek.length > 0) ? rule.daysOfWeek : [startDate.day()];
        let advanced = false;
        while (!advanced && cursor.diff(startDate, 'weeks') < rule.interval) {
          cursor = cursor.add(1, 'day');
          if (days.includes(cursor.day())) {
            advanced = true;
          }
        }
        if (!advanced) {
          cursor = cursor.add(rule.interval - 1, 'weeks');
          cursor = cursor.startOf('week').add(days[0], 'days');
        }
        break;
      }
      case 'monthly': {
        cursor = cursor.add(rule.interval, 'months');
        if (rule.dayOfMonth) {
          const day = Math.min(rule.dayOfMonth, cursor.daysInMonth());
          cursor = cursor.date(day);
        }
        break;
      }
      case 'yearly': {
        cursor = cursor.add(rule.interval, 'years');
        break;
      }
      case 'custom': {
        cursor = cursor.add(rule.interval, 'days');
        break;
      }
      default:
        cursor = cursor.add(rule.interval, 'days');
    }

    if (isPastBoundary(cursor)) {
      break;
    }

    results.push(cursor);
  }

  return results;
}

export interface RecurrenceEditorProps {
  value?: RecurrenceRule | null;
  onChange?: (value: RecurrenceRule | null) => void;
  disabled?: boolean;
  startDate?: Date | null;
}

export function RecurrenceEditor({
  value,
  onChange,
  disabled,
  startDate
}: RecurrenceEditorProps): JSX.Element {
  const [enabled, setEnabled] = useState<boolean>(Boolean(value?.enabled));
  const [rule, setRule] = useState<RecurrenceRule>(() => normaliseRule(value));

  // 無料プラン判定
  const { data: company } = trpc.company.getCurrentCompany.useQuery();
  const isFreePlan = company?.subscriptionPlan === 'free';

  useEffect(() => {
    if (value) {
      const nextRule = normaliseRule(value);
      setRule(prev => (areRulesEqual(prev, nextRule) ? prev : nextRule));
      setEnabled(true);
    } else {
      setRule(prev => (areRulesEqual(prev, DEFAULT_RULE) ? prev : DEFAULT_RULE));
      setEnabled(false);
    }
  }, [value]);

  useEffect(() => {
    if (!enabled) {
      if (value !== null && value !== undefined) {
        onChange?.(null);
      }
      return;
    }

    const normalisedValue = value ? normaliseRule(value) : null;
    if (normalisedValue && areRulesEqual(rule, normalisedValue)) {
      return;
    }

    onChange?.(rule);
  }, [enabled, rule, onChange, value]);

  const effectiveStart = useMemo(() => {
    if (startDate && dayjs(startDate).isValid()) {
      return dayjs(startDate);
    }
    return dayjs();
  }, [startDate]);

  const occurrences = useMemo(() => {
    if (!enabled) {
      return [];
    }
    return calculateOccurrences(rule, effectiveStart, 5);
  }, [rule, enabled, effectiveStart]);

  const updateRule = <K extends keyof RecurrenceRule>(key: K, value: RecurrenceRule[K]) => {
    setRule(prev => ({
      ...prev,
      [key]: value ?? undefined
    }));
  };

  return (
    <Card size="small" variant="outlined">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {isFreePlan && (
          <Alert
            type="warning"
            showIcon
            icon={<CrownOutlined />}
            message="有料プラン限定機能"
            description="繰り返しスケジュールは有料プランでご利用いただけます。"
            style={{ marginBottom: 8 }}
          />
        )}
        <Space align="center">
          <Switch
            checked={enabled}
            onChange={(checked) => setEnabled(checked)}
            disabled={disabled || isFreePlan}
          />
          <Text>繰り返しを有効にする</Text>
          {isFreePlan && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              （有料プラン限定）
            </Text>
          )}
        </Space>

        {enabled && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={12}>
                <label>パターン</label>
                <Select
                  value={rule.pattern}
                  onChange={(pattern) => updateRule('pattern', pattern)}
                  options={PATTERN_OPTIONS}
                  disabled={disabled}
                />
              </Col>
              <Col span={12}>
                <label>間隔</label>
                <NumericInput
                  min={1}
                  value={rule.interval}
                  onChange={(val) => {
                    const next = typeof val === 'number' ? val : Number(val);
                    updateRule('interval', next || 1);
                  }}
                  disabled={disabled}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            {rule.pattern === 'weekly' && (
              <div>
                <label>曜日</label>
                <Checkbox.Group
                  options={WEEKDAY_OPTIONS}
                  value={rule.daysOfWeek}
                  onChange={(values) => updateRule('daysOfWeek', values as number[])}
                  disabled={disabled}
                />
              </div>
            )}

            {rule.pattern === 'monthly' && (
              <div>
                <label>日付</label>
                <NumericInput
                  min={1}
                  max={31}
                  value={rule.dayOfMonth ?? effectiveStart.date()}
                  onChange={(val) => {
                    const next = typeof val === 'number' ? val : Number(val);
                    updateRule('dayOfMonth', Number.isFinite(next) && next >= 1 ? next : undefined);
                  }}
                  disabled={disabled}
                />
              </div>
            )}

            <Row gutter={16}>
              <Col span={12}>
                <label>終了日</label>
                <DatePicker
                  allowClear
                  value={rule.endDate ? dayjs(rule.endDate) : null}
                  onChange={(date) => updateRule('endDate', date ? date.endOf('day').toDate() : undefined)}
                  disabled={disabled}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <label>先行生成日数</label>
                <NumericInput
                  min={1}
                  max={365}
                  value={rule.advanceDays ?? 90}
                  onChange={(val) => {
                    const next = typeof val === 'number' ? val : Number(val);
                    updateRule('advanceDays', next || 90);
                  }}
                  disabled={disabled}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            <Space align="center">
              <Switch
                checked={rule.includeChildren ?? false}
                onChange={(checked) => updateRule('includeChildren', checked)}
                disabled={disabled}
              />
              <Text>子スケジュールも複製する</Text>
            </Space>

            <div>
              <Text type="secondary">次回発生プレビュー</Text>
              {occurrences.length === 0 ? (
                <div style={{ padding: 8 }}>
                  <Text type="secondary">有効な繰り返しが生成されません。</Text>
                </div>
              ) : (
                <List
                  size="small"
                  dataSource={occurrences}
                  renderItem={(item, index) => (
                    <List.Item key={index}>
                      {`${item.format('YYYY/MM/DD')} (${WEEKDAY_LABELS[item.day()]}) ${item.format('HH:mm')}`}
                    </List.Item>
                  )}
                />
              )}
            </div>
          </Space>
        )}
      </Space>
    </Card>
  );
}
